import { useState, type FormEvent } from "react";
import { Flag, X } from "lucide-react";
import { track } from "@/lib/analytics";
import {
  useRoundReport,
  type ReportReason,
  type RoundTargetType,
} from "@/lib/rounds";

const REASONS: Array<{ value: ReportReason; label: string }> = [
  { value: "patient_identifying", label: "Patient-identifying information" },
  { value: "defamation", label: "Defamation" },
  { value: "dosing_advice", label: "Case-specific dosing advice" },
  { value: "commercial_spam", label: "Commercial promotion or spam" },
  { value: "off_topic", label: "Off topic" },
  { value: "hate_harassment", label: "Harassment or hate" },
  { value: "other", label: "Other" },
];

export function ReportMenu({
  citySlug,
  targetType,
  targetId,
}: {
  citySlug: string;
  targetType: RoundTargetType;
  targetId: string;
}) {
  const report = useRoundReport(citySlug);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>("patient_identifying");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    report.mutate(
      { targetType, targetId, reason, note: note.trim() },
      {
        onSuccess: () => {
          setSent(true);
          setOpen(false);
          track("report", { target_type: targetType, target_id: targetId });
        },
      },
    );
  };

  if (sent) return <span className="pb-rounds-report-sent" role="status">Reported for review</span>;

  return (
    <div className="pb-rounds-report">
      <button type="button" onClick={() => setOpen((current) => !current)}>
        {open ? <X size={13} aria-hidden="true" /> : <Flag size={13} aria-hidden="true" />}
        {open ? "Close" : "Report"}
      </button>
      {open ? (
        <form onSubmit={submit}>
          <label>
            Reason
            <select value={reason} onChange={(event) => setReason(event.target.value as ReportReason)}>
              {REASONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label>
            Note <span>optional</span>
            <textarea maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <button className="pb-rounds-secondary" type="submit" disabled={report.isPending}>
            {report.isPending ? "Sending…" : "Send report"}
          </button>
          {report.isError ? <p role="alert">The report could not be sent. It may already be recorded.</p> : null}
        </form>
      ) : null}
    </div>
  );
}
